import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  Col,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  Row,
  cn,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideAlertTriangle,
  LucideCheckCircle,
  LucideInfo,
  LucideLock,
} from 'lucide-react';

import { TrialCloseChecklistItemRecord } from '../../_graphql/queryInventoryPeriodTrialClose';
import * as state from '../../_state/periodDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

type PeriodLockDialogProps = {
  periodName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

type ChecklistItemRowProps = {
  item: TrialCloseChecklistItemRecord;
};

const ChecklistItemRow = ({ item }: ChecklistItemRowProps) => (
  <Row
    className={cn(
      '@ChecklistItemRow',
      cn(
        'items-start gap-3 rounded-lg border p-3',
        item.blocking
          ? 'border-warning/30 bg-warning/5'
          : 'border-neutral/10 bg-neutral/2',
      ),
    )}
  >
    <div className="mt-0.5 shrink-0">
      {item.blocking ? (
        <LucideAlertTriangle className="size-4 text-warning" />
      ) : item.count === 0 ? (
        <LucideCheckCircle className="size-4 text-success" />
      ) : (
        <LucideInfo className="size-4 text-neutral/40" />
      )}
    </div>
    <Col className="min-w-0 flex-1" gap1>
      <Row className="items-center justify-between">
        <span
          className={cn(
            'text-sm font-medium',
            item.blocking && 'text-warning-darker',
          )}
        >
          {item.description}
        </span>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
            item.blocking
              ? 'bg-warning/15 text-warning-darker'
              : item.count === 0
                ? 'bg-success/10 text-success'
                : 'bg-neutral/10 text-neutral/60',
          )}
        >
          {item.count}
        </span>
      </Row>
      {item.details.length > 0 && (
        <ul className="text-xs text-neutral/50">
          {item.details.map((detail, idx) => (
            <li key={idx}>{detail}</li>
          ))}
        </ul>
      )}
    </Col>
  </Row>
);

export const PeriodLockDialog = ({
  periodName,
  isOpen,
  onClose,
  onConfirm,
}: PeriodLockDialogProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    data: trialCloseResult,
    isFetching,
    error,
  } = useAtomValue(state.$remoteTrialClose);

  const hasBlockingItems = React.useMemo(
    () =>
      trialCloseResult?.checklistItems?.some((item) => item.blocking) ?? false,
    [trialCloseResult],
  );

  const handleConfirm = React.useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirm]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className="@PeriodLockDialog"
    >
      <DialogContent className="max-w-lg min-w-[420px]" closeButton={false}>
        <DialogHeader className="mr-0">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <LucideLock className="size-5 shrink-0 text-primary" />
            {t.periodsListView.lockDialog.title}
          </div>
          <div className="text-sm text-neutral/75">
            {t.periodsListView.lockDialog.description(periodName)}
          </div>
        </DialogHeader>

        <div className="px-4">
          <AsyncBoundary error={error} loading={isFetching}>
            <Col gap2>
              <div className="text-xs font-medium tracking-wider text-neutral/40 uppercase">
                {t.periodsListView.lockDialog.checklistTitle}
              </div>
              {trialCloseResult?.checklistItems?.map((item, idx) => (
                <ChecklistItemRow key={idx} item={item} />
              ))}
              {hasBlockingItems && (
                <Row className="items-center gap-2 rounded-lg bg-warning/10 px-3 py-2">
                  <LucideAlertTriangle className="size-4 shrink-0 text-warning" />
                  <span className="text-xs text-warning-darker">
                    {t.periodsListView.lockDialog.blockingWarning}
                  </span>
                </Row>
              )}
            </Col>
          </AsyncBoundary>
        </div>

        <div className="flex justify-end gap-2 px-4 py-4">
          <Button
            variant="text"
            color="neutral"
            onPress={onClose}
            isDisabled={isSubmitting}
          >
            {t.periodsListView.lockDialog.cancel}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onPress={handleConfirm}
            isDisabled={hasBlockingItems || isSubmitting || isFetching}
            isLoading={isSubmitting}
          >
            {t.periodsListView.lockDialog.confirm}
          </Button>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
